/**
 * PraticaRow — F-006 Phase 5 (IMPL-P5-005).
 *
 * Single virtualized row of the archivio table. Rendered absolute-positioned
 * inside the virtualizer scroll container.
 *
 * Accessibility: each row carries `role="row"` + `aria-rowindex` so the
 * virtualized list still exposes a coherent logical position to assistive
 * tech (which can't infer row indices from absolute positioning alone).
 */
import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import type { PraticaListItem } from '@app/shared';
import { formatDate } from '../lib/format';

const COL_WIDTHS = {
  data: '12%',
  tipologia: '14%',
  proprietario: '20%',
  inquilino: '20%',
  indirizzo: '22%',
  stato: '12%',
} as const;

interface PraticaRowProps {
  item: PraticaListItem;
  rowIndex: number;
  style: CSSProperties;
}

export function PraticaRow({ item, rowIndex, style }: PraticaRowProps) {
  return (
    <div
      role="row"
      aria-rowindex={rowIndex}
      style={style}
      className="flex items-center border-b px-4 text-sm hover:bg-muted/40"
    >
      <span role="cell" style={{ width: COL_WIDTHS.data }}>
        <time dateTime={item.createdAt}>{formatDate(item.createdAt)}</time>
      </span>
      <span role="cell" style={{ width: COL_WIDTHS.tipologia }} className="truncate">
        {item.tipologia}
      </span>
      <span role="cell" style={{ width: COL_WIDTHS.proprietario }} className="truncate">
        {item.proprietarioNome}
      </span>
      <span role="cell" style={{ width: COL_WIDTHS.inquilino }} className="truncate">
        {item.inquilinoNome}
      </span>
      <span role="cell" style={{ width: COL_WIDTHS.indirizzo }} className="truncate">
        {item.indirizzoImmobile}
      </span>
      <span role="cell" style={{ width: COL_WIDTHS.stato }} className="flex items-center gap-2">
        <span className="inline-flex items-center rounded-md border bg-muted px-2 py-0.5 text-xs">
          {item.stato}
        </span>
        <Link
          to={`/app/archivio/${item.id}`}
          className="ml-auto text-xs font-medium text-primary hover:underline"
          aria-label={`Apri pratica ${item.proprietarioNome} / ${item.inquilinoNome}`}
        >
          Apri
        </Link>
      </span>
    </div>
  );
}

export { COL_WIDTHS };
